/**
 * @lodex-pro/web-shared/auth/refresh-gate — the ONE way a web tab calls POST /v1/auth/refresh.
 *
 * OPEN-193. A web-admin tab whose access token expired POSTed /v1/auth/refresh about 10 times
 * a second for 7 minutes (3,818 × 429). `useAuth` re-ran its restore on every Amplify auth
 * event, a failed refresh raised another event, and nothing honoured the 429. The limit is
 * per IP, so one runaway tab blocked refresh for every app behind the same office IP.
 *
 * Every refresh in the tab (authedFetch's 401 path and useAuth's boot-time restore) goes
 * through `refreshViaCookie`, which:
 *   - shares ONE in-flight request per tab: concurrent callers await the same promise;
 *   - after a 429, waits for `Retry-After` (seconds or an HTTP date) or an exponential backoff
 *     (1 s, 2 s, 4 s … capped at 60 s), whichever is longer. Calls during the wait resolve
 *     null at once, with no request;
 *   - after `REFRESH_MAX_FAILURES` failures in a row (429, 5xx, network, unusable body), or
 *     at once when the server says the refresh cookie is missing or expired (401/400/403),
 *     STOPS: no further requests until `resetRefreshGate()` (a new sign-in). Listeners
 *     registered with `onRefreshStopped` hear it, so `useAuth` can show the signed-out state.
 *
 * The refresh token is the HttpOnly `lodex_refresh` cookie, so Amplify cannot refresh by
 * itself; this module never touches Amplify. Callers pass `prime`, which stores the new access
 * token where Amplify reads it (amplify-bootstrap's `primeAccessTokenFromRefresh`). It runs
 * once per successful refresh, not once per caller.
 */

/** Consecutive failures after which the gate stops until the next sign-in. */
export const REFRESH_MAX_FAILURES = 5;
/** First backoff after a failure; doubles with each further failure. */
export const REFRESH_BACKOFF_BASE_MS = 1_000;
/** Ceiling for the exponential backoff and for an honoured `Retry-After`. */
export const REFRESH_BACKOFF_CAP_MS = 60_000;

let inFlight: Promise<string | null> | null = null;
let failures = 0;
let notBefore = 0;
let stopped = false;
const stopListeners = new Set<() => void>();

/** `Retry-After` in ms (delta-seconds or an HTTP date), or null when absent or unparseable. */
function retryAfterMs(res: Response, now: number): number | null {
  const raw = res.headers.get('Retry-After');
  if (raw === null || raw.trim() === '') return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

function stop(): void {
  if (stopped) return;
  stopped = true;
  for (const listener of stopListeners) listener();
}

/** Record one failed attempt and schedule the next allowed one. Always resolves null. */
function fail(serverWaitMs: number | null): null {
  failures += 1;
  if (failures >= REFRESH_MAX_FAILURES) {
    stop();
    return null;
  }
  const backoff = Math.min(REFRESH_BACKOFF_BASE_MS * 2 ** (failures - 1), REFRESH_BACKOFF_CAP_MS);
  const wait = Math.min(Math.max(backoff, serverWaitMs ?? 0), REFRESH_BACKOFF_CAP_MS);
  notBefore = Date.now() + wait;
  return null;
}

/**
 * Obtain a fresh access token from the refresh cookie. Resolves the token, or null when the
 * refresh failed, is backing off, or has stopped. Never throws.
 */
export function refreshViaCookie(
  baseUrl: string,
  prime: (accessToken: string) => void,
): Promise<string | null> {
  // The check and the assignment below are synchronous, so callers that arrive before the
  // refresh settles all get this same promise.
  if (inFlight !== null) return inFlight;
  if (stopped || Date.now() < notBefore) return Promise.resolve(null);

  inFlight = (async (): Promise<string | null> => {
    let res: Response;
    try {
      res = await fetch(`${baseUrl}/v1/auth/refresh`, { method: 'POST', credentials: 'include' });
    } catch {
      return fail(null); // offline or DNS: transient
    }
    if (res.status === 429) return fail(retryAfterMs(res, Date.now()));
    if (res.status === 400 || res.status === 401 || res.status === 403) {
      // No usable refresh cookie: the user is signed out. Retrying cannot fix that.
      stop();
      return null;
    }
    if (!res.ok) return fail(null); // 5xx: transient

    let body: { accessToken?: unknown };
    try {
      body = (await res.json()) as { accessToken?: unknown };
    } catch {
      return fail(null);
    }
    if (typeof body.accessToken !== 'string' || body.accessToken.length === 0) return fail(null);

    failures = 0;
    notBefore = 0;
    // Best-effort: authedFetch's retry also sends the token as a Bearer, so a failed prime
    // only leaves Amplify one call behind.
    prime(body.accessToken);
    return body.accessToken;
  })().finally(() => {
    // Clear once settled, so the next expiry starts a fresh refresh.
    inFlight = null;
  });
  return inFlight;
}

/** True once the gate has stopped (until `resetRefreshGate`). */
export function isRefreshStopped(): boolean {
  return stopped;
}

/** Listen for the gate stopping. Returns the unsubscribe. */
export function onRefreshStopped(listener: () => void): () => void {
  stopListeners.add(listener);
  return () => {
    stopListeners.delete(listener);
  };
}

/**
 * Clear failures, backoff and the stopped state. Called on a new sign-in (Amplify `signedIn`),
 * which sets a fresh refresh cookie.
 */
export function resetRefreshGate(): void {
  failures = 0;
  notBefore = 0;
  stopped = false;
}

/** Test-only: also drops an in-flight request and every listener. */
export function __resetRefreshGateForTesting(): void {
  resetRefreshGate();
  inFlight = null;
  stopListeners.clear();
}
